import { Alert, Group, Stack, Title } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconCalendar } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import Badge from 'components/Badge';
import Breadcrumbs from 'components/BreadcrumbsComponent';
import LoaderCoursePlanDetail from 'modules/pages/lcms/coursePlan/LoaderCoursePlanDetail';
import BasicPlanInformationSection from 'modules/pages/lcms/coursePlan/BasicPlanInformationSection';
import PlanCoursesSection from 'modules/pages/lcms/coursePlan/PlanCoursesSection';
import PlanScheduleSection from 'modules/pages/lcms/coursePlan/PlanScheduleSection';
import PlanRequirementsSection from 'modules/pages/lcms/coursePlan/PlanRequirementsSection';
import PlanTargetPositionsSection from 'modules/pages/lcms/coursePlan/PlanTargetPositionsSection';
import PlanBusinessUnitsSection from 'modules/pages/lcms/coursePlan/PlanBusinessUnitsSection';
import PlanJobFamilyGroupsSection from 'modules/pages/lcms/coursePlan/PlanJobFamilyGroupsSection';
import { useParams } from 'react-router-dom';
import { pathnames } from 'routes/lcms';
import { coursePlanService } from 'services/lcms/coursePlanService';
import PlanJobFamiliesSection from 'modules/pages/lcms/coursePlan/PlanJobFamiliesSection';

const DetailCoursePlanPage = () => {
  const params = useParams();
  const isDesktop = useMediaQuery('(min-width: 1441px)');
  const isTablet = useMediaQuery('(min-width: 757px)');
  const { id } = params;

  const { data, isLoading } = useQuery({
    queryKey: ['coursePlanDetail', id],
    queryFn: () => coursePlanService.getCoursePlanDetail(id as string),
    select: (response) => response.data
  });

  const breadcrumbs = [
    { title: 'Course Planning', href: pathnames.coursePlan },
    { title: 'Detail Course Plan', href: '' }
  ];

  const detailData = data;

  const uniqueCourses = Object.values(
    (detailData?.courses || []).reduce<
      Record<string, NonNullable<typeof detailData>['courses'][number]>
    >((acc, course) => {
      const courseKey = course.msLCMSCourseID || course.trLCMSPlanningCoursesID;

      if (!courseKey || acc[courseKey]) {
        return acc;
      }

      acc[courseKey] = course;
      return acc;
    }, {})
  );

  const totalLearningHours = uniqueCourses.reduce(
    (sum, course) => sum + (course.learningHours || 0),
    0
  );

  return (
    <>
      <Breadcrumbs items={breadcrumbs} mb={32} />
      {isLoading && <LoaderCoursePlanDetail />}
      {!isLoading && detailData && (
        <>
          <Stack gap={4}>
            <Group justify='space-between'>
              <Group align='center'>
                <Title order={2}>{detailData.planningName}</Title>
                <Badge styleVariant='outline' bg='blue'>
                  {detailData.programType}
                </Badge>
              </Group>
            </Group>
          </Stack>

          <Group mt={24} align='flex-start' pos={'relative'}>
            <Stack maw={isDesktop ? 970 : isTablet ? 750 : 'unset'} flex={1}>
              <Alert variant='light' icon={<IconCalendar />} color='blue'>
                Planning period: {new Date(detailData.planningStart).toLocaleDateString()} -{' '}
                {new Date(detailData.planningEnd).toLocaleDateString()}
              </Alert>

              <BasicPlanInformationSection
                planningData={{
                  planningName: detailData.planningName,
                  programType: detailData.programType,
                  planningDescription: detailData.planningDescription,
                  userIn: detailData.userIn,
                  dateIn: detailData.dateIn,
                  userUp: detailData.userUp,
                  dateUp: detailData.dateUp
                }}
              />
              <PlanCoursesSection courses={uniqueCourses} totalLearningHours={totalLearningHours} />
              <PlanBusinessUnitsSection businessUnits={detailData.businessUnits || []} />
              <PlanJobFamilyGroupsSection jobFamilyGroups={detailData.jobFamilyGroups || []} />
              <PlanJobFamiliesSection jobFamilies={detailData.jobFamilies || []} />
            </Stack>

            <Stack
              w={isDesktop ? 412 : isTablet ? 'fit-content' : '100%'}
              pos={isDesktop || isTablet ? 'sticky' : 'relative'}
              top={16}
            >
              <PlanScheduleSection
                planningStart={detailData.planningStart}
                planningEnd={detailData.planningEnd}
              />

              <PlanRequirementsSection
                prerequisites={detailData.prerequisites}
                learningMaterials={detailData.learningMaterials}
                facilitiesRequired={detailData.facilitiesRequired}
                notes={detailData.notes}
              />

              {/* Target Positions moved to sidebar and made optional */}
              {detailData.targetPositions && detailData.targetPositions.length > 0 && (
                <PlanTargetPositionsSection targetPositions={detailData.targetPositions} />
              )}
            </Stack>
          </Group>
        </>
      )}
    </>
  );
};

export default DetailCoursePlanPage;
